import { clsx } from 'clsx';
import { TabsContent } from 'src/components/ui/tabs';
import React, { useEffect, useRef, useState } from 'react';
import { FullScreenSystemPromptButton } from 'src/pages/ConfigurationPage/SystemPromptTab/FullScreenSystemPromptButton/FullScreenSystemPromptButton';
import { Controller, useFormContext } from 'react-hook-form';
import { Skeleton } from 'src/components/ui/skeleton';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { SystemPromptTemplateSelect } from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTemplateSelect/SystemPromptTemplateSelect';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';

export const SystemPromptTab = ({
  isCustomActiveSettings = false,
  configurationSettingsPrompt,
  customTemplatesData,
  readonly,
  isLoading,
  fullScreenBtnClick,
  onEditClick = () => {},
}) => {
  const [customTemplateOptions, setCustomTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [plateInitialValue, setPlateInitialValue] = React.useState('');
  const tabRef = useRef(null);

  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();

  useEffect(() => {
    if (customTemplatesData) {
      const options = customTemplatesData.map((customTemplate) => {
        return {
          id: `${customTemplate.id}`,
          name: customTemplate.name,
          placeholder: customTemplate.prompt_parts[0].placeholder,
          text: customTemplate.prompt_parts[0].template.trim(),
        };
      });

      setCustomTemplateOptions(options);

      if (configurationSettingsPrompt && configurationSettingsPrompt.is_manual) {
        const promptText = configurationSettingsPrompt.text.trim();
        const placeholder = Object.keys(configurationSettingsPrompt.input_data)[0];

        setValue('text', promptText);
        setPlateInitialValue(promptText);
        setValue('template_id', 'custom');
        setValue('placeholder', placeholder);
        setValue('selected_template_id', configurationSettingsPrompt.template_id);

        return;
      }

      if (options[0]) {
        onCustomTemplateChange(options[0]);
        setValue('template_id', options[0].id);
      }
    }
  }, [configurationSettingsPrompt, customTemplatesData, readonly]);

  useEffect(() => {
    if (selectedTemplate) {
      setValue('selected_template_id', selectedTemplate.id || '');
    }
  }, [selectedTemplate]);

  const onCustomTemplateChange = (option) => {
    if (option) {
      setValue('text', option.text);
      setPlateInitialValue(option.text);
      setSelectedTemplate(option);
      setValue('placeholder', option.placeholder);
    }
  };

  return (
    <TabsContent
      ref={tabRef}
      className={clsx(
        'p-0 mt-0 h-full overflow-hidden pr-2',
        readonly && '[&_div>[role="toolbar"]]:hidden',
      )}
      value={'system-prompt'}
    >
      <div className="flex h-full flex-col items-start gap-2.5 w-full">
        <LoaderWrapper
          isLoading={isSubmitting || isLoading}
          loader={
            <div className="flex flex-col w-full gap-2.5">
              <Skeleton className="w-full h-[40px]" />
            </div>
          }
        >
          {isCustomActiveSettings ? (
            <div
              className={
                'bg-[#F5FCFE] rounded-sm-5 border border-[#009CB240] flex items-center justify-center p-7.5'
              }
            >
              <p className={'text-center text-base font-medium text-[#009CB2]'}>
                Custom configuration by CoSupport AI.
                <br /> To request edits, reach out to your Account Manager.
              </p>
            </div>
          ) : (
            <>
              <div className={'w-full flex flex-row items-center justify-between'}>
                <div className={'flex flex-row gap-2.5 items-center'}>
                  <p className="text-sm text-[#7A6BE5] font-bold">Prompt</p>
                  <SystemPromptTemplateSelect
                    readonly={readonly}
                    onChange={onCustomTemplateChange}
                    options={customTemplateOptions}
                  />
                </div>
                <FullScreenSystemPromptButton onClick={fullScreenBtnClick} />
              </div>
              <Controller
                control={control}
                name="text"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className={'h-full w-full pb-14'}>
                    <PlateEditor
                      readOnly={readonly}
                      label={''}
                      onClick={() => {
                        if (readonly) {
                          onEditClick();
                        }
                      }}
                      initialValue={plateInitialValue}
                      resetOnReadOnlyChange={true}
                      onChange={onChange}
                      error={error}
                      containerClassName={'h-[calc(100%-20px)] !overflow-hidden'}
                      className={'w-full max-w-full h-full'}
                    />
                  </div>
                )}
              />
            </>
          )}
        </LoaderWrapper>
      </div>
    </TabsContent>
  );
};
